/**
 * SOLID Review
 * S — Strong. One concern: keep the 3D scene's demand frame loop drawing after mount and after the page's layout changes.
 * O — Good. Another trigger is one more listener.
 * L — Strong. A view model (`IServiceViewModel`) with no state a view renders.
 * I — Strong. The view-model surface, the frame request its view hands it.
 * D — Strong. The platform's animation frames and page events.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { Reactions } from './Reactions';
import type { IConfigDepViewPlatform } from './platform/IConfigDepViewPlatform';

/** How many frames the warm-up pumps after mount. */
const WARMUP_FRAMES = 20;

/** What the warm-up's view hands it: the scene's frame request. */
type TWarmupInputs = readonly [invalidate: () => void];

const NO_STATE: Readonly<Record<string, never>> = Object.freeze({});

/**
 * THE DEMAND LOOP'S WARM-UP (frontend-framework-oo P4, 2026-10-29, §28.296) — `GameScene.tsx`'s `DemandWarmup` effect. It defeats the
 * iOS "blank scene in frameloop=demand" trap: iOS Safari can lay the canvas out at 0×0 on first paint, and in demand mode
 * react-three-fiber then renders exactly once (into nothing) and never again. So a short burst of frames is pumped after mount, and a
 * frame asked for on resize, orientation change and tab re-show, so the first real-sized layout is actually drawn — harmless on every
 * other device (a few extra frames once).
 */
export class FrameWarmup implements IServiceViewModel<typeof NO_STATE> {
  private readonly reactions = new Reactions<TWarmupInputs>([{
    deps: (inputs) => inputs,
    run: ([invalidate]) => {
      const { frames, pageEvents } = this.platform;
      let raf = 0;
      let n = 0;
      const pump = (): void => {
        invalidate();
        if (++n < WARMUP_FRAMES) raf = frames.request(pump);
      };
      raf = frames.request(pump);
      const kick = (): void => invalidate();
      const leaveResize = pageEvents.on('resize', kick);
      const leaveTurn = pageEvents.on('orientationchange', kick);
      const leaveShow = pageEvents.onDocument('visibilitychange', kick);
      return () => {
        frames.cancel(raf);
        leaveResize();
        leaveTurn();
        leaveShow();
      };
    },
  }]);

  constructor(private readonly platform: Pick<IConfigDepViewPlatform, 'frames' | 'pageEvents'>) {}

  getSnapshot(): typeof NO_STATE {
    return NO_STATE;
  }

  subscribe(): () => void {
    return () => undefined;
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as TWarmupInputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as TWarmupInputs);
  }
}
